import { 
  BioBoxType, 
  BioCrateType,
  BioBoxRarity, 
  BioCrateRarity,
  BioBoxMetadata, 
  BioCrateMetadata,
  LootItem, 
  DemographicTitle, 
  UserPerk, 
  VaultShopItem,
  DemographicCohort 
} from '../types';

export const RARITY_COLORS: Record<BioBoxRarity, { text: string; bg: string; border: string; glow: string; hex: string }> = {
  common: {
    text: 'text-slate-400',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/30',
    glow: 'rgba(148, 163, 184, 0.3)',
    hex: '#94A3B8'
  },
  rare: {
    text: 'text-[#00E5FF]',
    bg: 'bg-[#00E5FF]/10',
    border: 'border-[#00E5FF]/40',
    glow: 'rgba(0, 229, 255, 0.4)',
    hex: '#00E5FF'
  },
  epic: {
    text: 'text-[#A855F7]',
    bg: 'bg-[#A855F7]/10',
    border: 'border-[#A855F7]/40',
    glow: 'rgba(168, 85, 247, 0.45)',
    hex: '#A855F7'
  },
  legendary: {
    text: 'text-[#FFB300]',
    bg: 'bg-[#FFB300]/10',
    border: 'border-[#FFB300]/50',
    glow: 'rgba(255, 179, 0, 0.5)',
    hex: '#FFB300'
  },
  mythic: {
    text: 'text-[#00E676]',
    bg: 'bg-[#00E676]/15',
    border: 'border-[#00E676]',
    glow: 'rgba(0, 230, 118, 0.6)',
    hex: '#00E676'
  }
};

export const RARITY_WEIGHTS: Record<BioBoxRarity, number> = {
  common: 5500,    // 55.0%
  rare: 2800,      // 28.0%
  epic: 1200,      // 12.0%
  legendary: 420,  // 4.2%
  mythic: 80       // 0.8%
};

export const BIO_BOX_DEFINITIONS: Record<BioBoxType, BioBoxMetadata> = {
  daily_supply: {
    id: 'daily_supply',
    name: {
      ua: 'Щоденний Біо-Бокс Припасів',
      en: 'Daily Supply Bio-Box'
    },
    tagline: {
      ua: 'Базовий запас нутрієнтів та токенів на день',
      en: 'Daily baseline nutrient boost & token payload',
      broUA: 'Забирай свій щоденний хабар, бро! ⚡',
      broEN: 'Daily drop is primed, homie! Lock in ⚡'
    },
    description: {
      ua: 'Надається щодня за перший запис прийому їжі або води.',
      en: 'Awarded daily upon your first recorded meal or hydration log.'
    },
    icon: 'PackageCheck',
    primaryColor: '#00E5FF',
    glowColor: 'rgba(0, 229, 255, 0.4)',
    minTokens: 25,
    maxTokens: 150,
    guaranteedRarity: 'common',
    sourceCriteria: {
      ua: '1 прийом їжі або напій на день',
      en: '1 meal or water log per day'
    }
  },
  streak_matrix: {
    id: 'streak_matrix',
    name: {
      ua: 'Матричний Стрік-Бокс',
      en: 'Streak Matrix Bio-Box'
    },
    tagline: {
      ua: 'Нагорода за залізну дисципліну та послідовність',
      en: 'High-yield matrix reward for unbroken discipline',
      broUA: 'Твій стрік горить пекельним вогнем! 🔥',
      broEN: 'Streak is straight fire! Keep the momentum 🔥'
    },
    description: {
      ua: 'Видається на ключових рубежах стріку. Містить захист стріку та рідкісні перки.',
      en: 'Granted at milestone streak checkpoints. Contains Streak Freezes & rare perks.'
    },
    icon: 'Flame',
    primaryColor: '#FFB300',
    glowColor: 'rgba(255, 179, 0, 0.45)',
    minTokens: 100,
    maxTokens: 500,
    guaranteedRarity: 'rare',
    sourceCriteria: {
      ua: 'Досягнення ключових рубежів стріку',
      en: 'Hitting milestone streak checkpoints'
    }
  },
  perfect_macro: {
    id: 'perfect_macro',
    name: {
      ua: 'Сейф Ідеального Макросу',
      en: 'Perfect Macro Bio-Box Vault'
    },
    tagline: {
      ua: 'Елітне сховище за 100% точність білків, жирів та калорій',
      en: 'Elite cyber-vault for precision macro calibration',
      broUA: 'Макроси тютелька в тютельку! Абсолютний Apex рівень! 👑',
      broEN: 'Hit macros dead-on! You are an absolute machine! 👑'
    },
    description: {
      ua: 'Нараховується при попаданні в калорійну та макро-норму з точністю ±5%. Містить PRO-пропуски та міфічні релікти.',
      en: 'Unlocked when daily calories and macros land within ±5% tolerance. Contains PRO Passes & mythic relics.'
    },
    icon: 'Sparkles',
    primaryColor: '#00E676',
    glowColor: 'rgba(0, 230, 118, 0.5)',
    minTokens: 250,
    maxTokens: 1000,
    guaranteedRarity: 'epic',
    sourceCriteria: {
      ua: 'Калорії та БЖВ у межах ±5% від денної цілі',
      en: 'Calories & macros within ±5% of daily target'
    }
  }
};

export const BIO_CRATE_DEFINITIONS = BIO_BOX_DEFINITIONS; // Alias for compatibility


export const DEMOGRAPHIC_TITLES_MATRIX: DemographicTitle[] = [
  // --- GEN Z (16 - 25) ---
  {
    id: 'genz_t1_m',
    tier: 1,
    cohort: 'gen_z',
    gender: 'male',
    minLogs: 0,
    title: { en: 'Sigma Cadet', ua: 'Сигма Кадет' },
    systemDesc: { en: 'Basic nutrition tracking initiated. Level 1 telemetry online.', ua: 'Базовий трекінг розпочато. Телеметрія 1 рівня активована.' },
    broDesc: { en: 'Day 1 locked in. Time to mog your daily calories.', ua: 'День перший пішов. Час розвалювати макроси по красі.' },
    perks: ['token_boost_5'],
    neonColor: '#00E5FF',
    rankBadgeIcon: 'Utensils'
  },
  {
    id: 'genz_t1_f',
    tier: 1,
    cohort: 'gen_z',
    gender: 'female',
    minLogs: 0,
    title: { en: 'Valkyrie Initiate', ua: 'Валькірія Новачок' },
    systemDesc: { en: 'Initial metabolic logging established.', ua: 'Початковий метаболічний журнал створено.' },
    broDesc: { en: 'Glow-up arc activated! Logging your first energy source.', ua: 'Арка преображення активна! Записуємо першу енергію.' },
    perks: ['token_boost_5'],
    neonColor: '#00E5FF',
    rankBadgeIcon: 'Utensils'
  },
  {
    id: 'genz_t2_all',
    tier: 2,
    cohort: 'gen_z',
    gender: 'all',
    minLogs: 3,
    title: { en: 'Macro Fiend', ua: 'Макро Дроппер' },
    systemDesc: { en: 'Consistent 3+ meal inputs catalogued. Metabolic awareness validated.', ua: 'Зафіксовано 3+ страви. Метаболічна стабільність підтверджена.' },
    broDesc: { en: 'No cap, your logging form is dialed. 3 entries deep!', ua: 'Без жартів, форма трекінгу топова. 3 прийоми в кишені!' },
    perks: ['token_boost_5', 'crate_luck_tier1'],
    neonColor: '#3B82F6',
    rankBadgeIcon: 'Shield'
  },
  {
    id: 'genz_t3_m',
    tier: 3,
    cohort: 'gen_z',
    gender: 'male',
    minLogs: 10,
    title: { en: 'Sigma Shredder', ua: 'Сигма Мезоморф' },
    systemDesc: { en: 'High-frequency macro execution. Optimal body composition phase.', ua: 'Високочастотний макро-контроль. Фаза оптимізації композиції тіла.' },
    broDesc: { en: 'Literal gym demon energy. You are on an absolute tear 🔥', ua: 'Справжній звір тренажерки. Стрік на максимумі, форма космос 🔥' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'genz_t3_f',
    tier: 3,
    cohort: 'gen_z',
    gender: 'female',
    minLogs: 10,
    title: { en: 'Valkyrie Shredder', ua: 'Валькірія Шреддер' },
    systemDesc: { en: 'High-frequency macro execution. Lean endurance phase.', ua: 'Високочастотний макро-контроль. Фаза рельєфу та витривалості.' },
    broDesc: { en: 'Main character energy. Macro targets getting demolished daily ✨', ua: 'Енергія головної героїні! Макроси закриваються з легкістю ✨' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'genz_t4_all',
    tier: 4,
    cohort: 'gen_z',
    gender: 'all',
    minLogs: 25,
    title: { en: 'Apex GigaChad', ua: 'Апекс Гігачад' },
    systemDesc: { en: 'Peak demographic rank. Uncompromised nutritional mastery.', ua: 'Найвищий демографічний ранг. Бездоганна нутриціологічна дисципліна.' },
    broDesc: { en: 'Ascended to godhood. Your stats are maxed out, king 👑', ua: 'Вихід на божественний рівень. Твої статки викручені на 100% 👑' },
    perks: ['token_boost_10', 'crate_luck_tier2', 'streak_shield_discount', 'bio_avatar_aura'],
    neonColor: '#00E676',
    rankBadgeIcon: 'Crown'
  },

  // --- MILLENNIALS (26 - 40) ---
  {
    id: 'mil_t1_all',
    tier: 1,
    cohort: 'millennial',
    gender: 'all',
    minLogs: 0,
    title: { en: 'Desk Warrior', ua: 'Офісний Атлет' },
    systemDesc: { en: 'Sedentary offset routine initiated. Basic caloric ledger opened.', ua: 'Режим компенсації малорухливості розпочато. Базовий баланс відкрито.' },
    broDesc: { en: 'Balancing the grind with real macros. First dish logged!', ua: 'Балансуємо роботу та здорове харчування. Перша страва в журналі!' },
    perks: ['token_boost_5'],
    neonColor: '#00E5FF',
    rankBadgeIcon: 'Utensils'
  },
  {
    id: 'mil_t2_all',
    tier: 2,
    cohort: 'millennial',
    gender: 'all',
    minLogs: 3,
    title: { en: 'Macro Manager', ua: 'Майстер Макросу' },
    systemDesc: { en: 'Systematic nutrition distribution across active working cycles.', ua: 'Систематичний розподіл нутрієнтів у робочому циклі.' },
    broDesc: { en: 'Project management applied to your physique. 3 clean meals logged!', ua: 'Менеджмент макросів на висоті. 3 чисті прийоми їжі!' },
    perks: ['token_boost_5', 'crate_luck_tier1'],
    neonColor: '#3B82F6',
    rankBadgeIcon: 'Shield'
  },
  {
    id: 'mil_t3_m',
    tier: 3,
    cohort: 'millennial',
    gender: 'male',
    minLogs: 10,
    title: { en: 'Optimal Chad', ua: 'Оптимальний Чек' },
    systemDesc: { en: 'Data-driven physical performance. Metabolic efficiency sustained.', ua: 'Аналітичний контроль фізичної форми. Метаболічна ефективність досягнута.' },
    broDesc: { en: 'Peak optimization. You run on clean protein and zero excuses 💪', ua: 'Максимальна оптимізація. Чистий білок, енергія через край 💪' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'mil_t3_f',
    tier: 3,
    cohort: 'millennial',
    gender: 'female',
    minLogs: 10,
    title: { en: 'Optimal Athena', ua: 'Оптимальна Афіна' },
    systemDesc: { en: 'Data-driven physical performance. Metabolic symmetry sustained.', ua: 'Аналітичний контроль форми. Метаболічна симетрія досягнута.' },
    broDesc: { en: 'Peak balance and stamina. Crushing goals effortlessly ⚡', ua: 'Ідеальний баланс сили та енергії. Цілі закриваються на раз ⚡' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'mil_t4_all',
    tier: 4,
    cohort: 'millennial',
    gender: 'all',
    minLogs: 25,
    title: { en: 'Apex Bio-Architect', ua: 'Архітектор Біо-Ритмів' },
    systemDesc: { en: 'Full biometric mastery. Circadian and macro calibration synchronized.', ua: 'Повний біометричний контроль. Циркадні та макро-ритми синхронізовано.' },
    broDesc: { en: 'You literally engineered your dream metabolic machine. Legend! 👑', ua: 'Ти власноруч сконструював ідеальний метаболізм. Легенда! 👑' },
    perks: ['token_boost_10', 'crate_luck_tier2', 'streak_shield_discount', 'bio_avatar_aura'],
    neonColor: '#00E676',
    rankBadgeIcon: 'Crown'
  },

  // --- MASTERS / CLASSIC (41+) ---
  {
    id: 'mas_t1_all',
    tier: 1,
    cohort: 'masters',
    gender: 'all',
    minLogs: 0,
    title: { en: 'Longevity Initiate', ua: 'Адепт Довголіття' },
    systemDesc: { en: 'Cellular preservation and anti-inflammatory protocol active.', ua: 'Протокол клітинного збереження та зниження запалення активний.' },
    broDesc: { en: 'Bio-hacking your prime years. First smart meal logged!', ua: 'Біо-хакінг найкращих років. Перший розумний прийом їжі записано!' },
    perks: ['token_boost_5'],
    neonColor: '#00E5FF',
    rankBadgeIcon: 'Utensils'
  },
  {
    id: 'mas_t2_all',
    tier: 2,
    cohort: 'masters',
    gender: 'all',
    minLogs: 3,
    title: { en: 'Cellular Engineer', ua: 'Клітинний Інженер' },
    systemDesc: { en: 'Mitochondrial health optimization via targeted macronutrients.', ua: 'Оптимізація мітохондріального здоров\'я через цільові макронутрієнти.' },
    broDesc: { en: 'Building lasting cellular resilience. 3 consistent logs confirmed.', ua: 'Будуємо довговічну клітинну витривалість. 3 стабільні записи!' },
    perks: ['token_boost_5', 'crate_luck_tier1'],
    neonColor: '#3B82F6',
    rankBadgeIcon: 'Shield'
  },
  {
    id: 'mas_t3_m',
    tier: 3,
    cohort: 'masters',
    gender: 'male',
    minLogs: 10,
    title: { en: 'Executive Silverback', ua: 'Срібний Лідер' },
    systemDesc: { en: 'Apex hormonal and metabolic stability. High executive vitality index.', ua: 'Найвища гормональна та метаболічна стабільність. Високий індекс вітальності.' },
    broDesc: { en: 'Old school grit meets modern bio-tech. You run the room and the gym 🦍', ua: 'Сталевий характер плюс новітній біо-хак. Авторитет і форма на максимумі 🦍' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'mas_t3_f',
    tier: 3,
    cohort: 'masters',
    gender: 'female',
    minLogs: 10,
    title: { en: 'Empress Prime', ua: 'Імператриця Прайм' },
    systemDesc: { en: 'Apex metabolic longevity and bone mineral density support.', ua: 'Найвища метаболічна довговічність та підтримка щільності кісткової тканини.' },
    broDesc: { en: 'Ageless grace with unstoppable stamina. Total inspiration ✨', ua: 'Енергія поза віком та незламна витривалість. Справжнє натхнення ✨' },
    perks: ['token_boost_10', 'crate_luck_tier1', 'streak_shield_discount'],
    neonColor: '#A855F7',
    rankBadgeIcon: 'Flame'
  },
  {
    id: 'mas_t4_all',
    tier: 4,
    cohort: 'masters',
    gender: 'all',
    minLogs: 25,
    title: { en: 'Longevity Sovereign', ua: 'Апекс Патріарх Довголіття' },
    systemDesc: { en: 'Decelerated biological aging rate confirmed. Peak cellular dividend.', ua: 'Підтверджено сповільнення біологічного старіння. Максимальний дивіденд здоров\'я.' },
    broDesc: { en: 'Turned back the biological clock. Absolute master of the game 👑', ua: 'Повернув біологічний годинник назад. Абсолютний гросмейстер життя 👑' },
    perks: ['token_boost_10', 'crate_luck_tier2', 'streak_shield_discount', 'bio_avatar_aura'],
    neonColor: '#00E676',
    rankBadgeIcon: 'Crown'
  }
];

export const PERKS_REGISTRY: Record<string, UserPerk> = {
  token_boost_5: {
    id: 'token_boost_5',
    tierRequirement: 1,
    name: { en: 'Macro Yield +5%', ua: 'Макро-Урожай +5%' },
    desc: { en: 'Earn +5% bonus Nutri-Tokens on all meal & water logs', ua: '+5% додаткових Nutri-Токенів за всі записи їжі та води' },
    broFlavor: { en: 'Free token multiplier locked in, homie!', ua: 'Буст токенів активовано, бро!' },
    icon: 'Coins',
    tokenBonusPct: 5
  },
  token_boost_10: {
    id: 'token_boost_10',
    tierRequirement: 3,
    name: { en: 'Sigma Yield +10%', ua: 'Сигма-Урожай +10%' },
    desc: { en: 'Earn +10% bonus Nutri-Tokens on all logs and streak bonuses', ua: '+10% додаткових Nutri-Токенів за всі записи та стрік бонуси' },
    broFlavor: { en: 'Double token flow! Stack those gains.', ua: 'Подвійний потік токенів! Збирай урожай.' },
    icon: 'Zap',
    tokenBonusPct: 10
  },
  crate_luck_tier1: {
    id: 'crate_luck_tier1',
    tierRequirement: 2,
    name: { en: 'Lucky Drops +5%', ua: 'Щасливий Дроп +5%' },
    desc: { en: '+5% increased probability of Rare/Epic loot in Bio-Boxes', ua: '+5% до шансу випадіння рідкісного та епічного луту в Біо-Боксах' },
    broFlavor: { en: 'Bio-Boxes hit different when luck is on your side.', ua: 'Біо-Бокси відкриваються значно жирніше!' },
    icon: 'Sparkles',
    luckModifier: 1.05
  },
  crate_luck_tier2: {
    id: 'crate_luck_tier2',
    tierRequirement: 4,
    name: { en: 'Apex Roll +10%', ua: 'Апекс Рулетка +10%' },
    desc: { en: '+10% boost for Legendary & Mythic rewards in Bio-Boxes', ua: '+10% до шансу легендарних та міфічних нагород у Біо-Боксах' },
    broFlavor: { en: 'Guaranteed high-tier energy on every unboxing!', ua: 'Тільки топ-дроп при кожному анбоксінгу!' },
    icon: 'Crown',
    luckModifier: 1.10
  },
  streak_shield_discount: {
    id: 'streak_shield_discount',
    tierRequirement: 3,
    name: { en: 'Shield Mastery -25%', ua: 'Майстер Щита -25%' },
    desc: { en: '25% discount on Streak Freezes in the Redemption Vault', ua: '25% знижка на покупку заморозки стріку у сховищі' },
    broFlavor: { en: 'Keep your streak immortal on the cheap.', ua: 'Утримуй свій стрік безсмертним за копійки.' },
    icon: 'ShieldAlert',
    streakDiscountPct: 25
  },
  bio_avatar_aura: {
    id: 'bio_avatar_aura',
    tierRequirement: 4,
    name: { en: 'Neon Cyber Aura', ua: 'Неонова Кібер-Аура' },
    desc: { en: 'Exclusive glowing neon green avatar frame in profile and community', ua: 'Ексклюзивна неонова рамка аватара в профілі та спільноті' },
    broFlavor: { en: 'Everyone knows you are apex rank the second you drop in.', ua: 'Всі одразу бачать, хто тут батя кібер-фітнесу.' },
    icon: 'Flame'
  }
};

export const VAULT_SHOP_CATALOGUE: VaultShopItem[] = [
  {
    id: 'item_streak_freeze_1',
    category: 'boosters',
    name: { ua: 'Заморозка Стріку (1 день)', en: 'Streak Freeze (1 Day)' },
    desc: { ua: 'Захищає ваш стрік від скидання при пропуску одного дня записів.', en: 'Shields your active streak from breaking if you miss a day of logging.' },
    broFlavor: { ua: 'Не дай своєму вогню згаснути через важкий день!', en: 'Insurance for your streak. Zero stress, bro!' },
    tokenPrice: 150,
    rarity: 'rare',
    icon: 'Shield',
    actionType: 'add_consumable',
    payload: {
      consumableType: 'streak_freeze',
      quantity: 1
    }
  },
  {
    id: 'item_pro_pass_24h',
    category: 'passes',
    name: { ua: 'PRO Доступ (24 Години)', en: 'PRO Access Pass (24 Hours)' },
    desc: { ua: 'Миттєво відкриває всі PRO фічі, ШІ-аналіз, рецепти та біо-хакінг на 24 години.', en: 'Instantly grants full PRO access, AI Vision analysis, recipes, and bio-markers for 24 hours.' },
    broFlavor: { ua: 'Тестуй повний фарш ШІ-функцій на добу!', en: '24-hour turbo pass. Unlock the full AI toolkit!' },
    tokenPrice: 200,
    rarity: 'rare',
    icon: 'Zap',
    actionType: 'activate_pro',
    payload: {
      proDurationHours: 24
    }
  },
  {
    id: 'item_pro_pass_48h',
    category: 'passes',
    name: { ua: 'PRO Доступ (48 Годин)', en: 'PRO Access Pass (48 Hours)' },
    desc: { ua: 'Повний PRO пакет на 2 доби для інтенсивного відстеження макросів.', en: 'Full PRO suite for 2 days of intensive bio-marker tracking.' },
    broFlavor: { ua: 'Два дні повного домінування в залі та на кухні!', en: '48 hours of pure nutritional mastery!' },
    tokenPrice: 350,
    rarity: 'epic',
    icon: 'Crown',
    actionType: 'activate_pro',
    payload: {
      proDurationHours: 48
    }
  },
  {
    id: 'item_pro_pass_7d',
    category: 'passes',
    name: { ua: 'PRO Ваучер (7 Днів)', en: 'PRO 7-Day Voucher' },
    desc: { ua: 'Цілий тиждень преміального доступу до всіх модулів MAIN Nutrition.', en: 'A full 7-day master pass to all MAIN Nutrition premium modules.' },
    broFlavor: { ua: 'Тиждень божественного режиму за зароблені токени!', en: '7 days of godmode unlocked purely through discipline!' },
    tokenPrice: 900,
    rarity: 'legendary',
    icon: 'Sparkles',
    actionType: 'activate_pro',
    payload: {
      proDurationHours: 168
    }
  },
  {
    id: 'item_theme_cyberpunk',
    category: 'themes',
    name: { ua: 'Тема: Кіберпанк Матриця', en: 'Theme: Cyberpunk Matrix' },
    desc: { ua: 'Неонові смарагдові сітки, темний обсидіан та динамічні CRT глітчі.', en: 'Neon emerald wireframes, dark obsidian surfaces, and dynamic CRT glithes.' },
    broFlavor: { ua: 'Перетвори інтерфейс на пульт управління Нео!', en: 'Turn your app into the Matrix mainframe!' },
    tokenPrice: 500,
    rarity: 'epic',
    icon: 'Palette',
    actionType: 'unlock_theme',
    payload: {
      themeId: 'cyberpunk'
    }
  },
  {
    id: 'item_theme_stalker',
    category: 'themes',
    name: { ua: 'Тема: S.T.A.L.K.E.R. Hazmat', en: 'Theme: S.T.A.L.K.E.R. Hazmat' },
    desc: { ua: 'Автентичний монохромний PDA інтерфейс, лічильник Гейгера та радіоактивний зелений.', en: 'Authentic monochrome PDA interface, Geiger audio clicks, and radioactive green.' },
    broFlavor: { ua: 'Для справжніх бродяг Зони. Чистий хардкор!', en: 'Built for Zone survivors. Heavy hazmat telemetry!' },
    tokenPrice: 500,
    rarity: 'epic',
    icon: 'Radiation',
    actionType: 'unlock_theme',
    payload: {
      themeId: 'stalker'
    }
  },
  {
    id: 'item_crate_daily_pack',
    category: 'boxes',
    name: { ua: 'Набір Біо-Боксів (3x)', en: 'Supply Bio-Box Bundle (3x)' },
    desc: { ua: '3 Щоденні Біо-Бокси Припасів для миттєвого відкриття.', en: '3 Daily Supply Bio-Boxes ready for immediate unboxing.' },
    broFlavor: { ua: 'Потрійний дроп для швидкого прокачування!', en: 'Triple Bio-Box pack! Stack the rewards fast.' },
    tokenPrice: 250,
    rarity: 'rare',
    icon: 'PackageCheck',
    actionType: 'add_box',
    payload: {
      boxType: 'daily_supply',
      crateType: 'daily_supply',
      quantity: 3
    }
  },
  {
    id: 'item_crate_streak_matrix',
    category: 'boxes',
    name: { ua: 'Матричний Стрік-Бокс', en: 'Streak Matrix Bio-Box' },
    desc: { ua: 'Містить гарантовані рідкісні предмети, захист стріку та до 500 Nutri-Токенів.', en: 'Contains guaranteed Rare+ items, Streak Freezes, and up to 500 Nutri-Tokens.' },
    broFlavor: { ua: 'Купуй елітний Біо-Бокс прямо зараз, не чекаючи стріку!', en: 'Grab an elite streak Bio-Box right now on demand!' },
    tokenPrice: 400,
    rarity: 'epic',
    icon: 'Flame',
    actionType: 'add_box',
    payload: {
      boxType: 'streak_matrix',
      crateType: 'streak_matrix',
      quantity: 1
    }
  },
  {
    id: 'item_crate_perfect_macro',
    category: 'boxes',
    name: { ua: 'Сейф Ідеального Макросу', en: 'Perfect Macro Bio-Box Vault' },
    desc: { ua: 'Елітна скриня з шансом на PRO доступ, легендарні релікти та до 1000 Nutri-Токенів.', en: 'Elite cyber-vault with chances for PRO access passes, legendary relics, and up to 1000 NTK.' },
    broFlavor: { ua: 'Найжирніший Біо-Бокс у системі. Відкривай та насолоджуйся!', en: 'The fattest Bio-Box in the entire game economy!' },
    tokenPrice: 750,
    rarity: 'legendary',
    icon: 'Sparkles',
    actionType: 'add_box',
    payload: {
      boxType: 'perfect_macro',
      crateType: 'perfect_macro',
      quantity: 1
    }
  }
];

// Helper to determine demographic cohort from age
export function calculateDemographicCohort(age?: number, createdAtTimestamp?: number): DemographicCohort {
  if (typeof age === 'number' && age > 0) {
    if (age <= 25) return 'gen_z';
    if (age <= 40) return 'millennial';
    return 'masters';
  }
  return 'millennial';
}

// Find matching title for a user
export function resolveDemographicTitle(
  cohort: DemographicCohort,
  gender: 'male' | 'female' | 'other' | string,
  totalLogs: number
): DemographicTitle {
  const normGender = gender === 'female' ? 'female' : 'male';
  
  // Find all matching titles in user's cohort
  const cohortTitles = DEMOGRAPHIC_TITLES_MATRIX.filter(t => 
    t.cohort === cohort && (t.gender === normGender || t.gender === 'all')
  );

  // Sort by tier descending
  const sorted = cohortTitles.sort((a, b) => b.minLogs - a.minLogs);

  // Return the highest tier the user qualifies for
  for (const title of sorted) {
    if (totalLogs >= title.minLogs) {
      return title;
    }
  }

  return DEMOGRAPHIC_TITLES_MATRIX[0];
}
